import { createPortal } from 'react-dom'
import { useState, useEffect, useRef, useCallback } from 'react'
import { usePanelStore } from '../../../lib/panelStore'
import { ChevronDoubleRightIcon } from '../tasks/components/TaskIcons'
import { Upload, Trash2, Loader2 } from 'lucide-react'
import { useParams } from '@tanstack/react-router'
import { useUploadFile, useCreateFolder } from '@/hooks/useFiles'
import { useTranslation } from 'react-i18next'

interface FileUploadPanelProps {
  isOpen: boolean
  onClose: () => void
  folderId?: string | null
  initialFiles?: File[]
  onUploadComplete?: () => void
}

interface QueuedFile {
  id: string
  file: File
  relativePath: string
  progress: number
  status: 'pending' | 'uploading' | 'done' | 'error'
  error?: string
}

// Type definitions for File System API
interface FileSystemEntry {
  isFile: boolean
  isDirectory: boolean
  name: string
  fullPath: string
}

interface FileSystemFileEntry extends FileSystemEntry {
  file: (callback: (file: File) => void) => void
}

interface FileSystemDirectoryEntry extends FileSystemEntry {
  createReader: () => FileSystemDirectoryReader
}

interface FileSystemDirectoryReader {
  readEntries: (callback: (entries: FileSystemEntry[]) => void) => void
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB'
}

export function FileUploadPanel({
  isOpen,
  onClose,
  folderId,
  initialFiles,
  onUploadComplete,
}: FileUploadPanelProps) {
  const { workspaceSlug } = useParams({ from: '/$workspaceSlug' })
  const setIsPanelOpen = usePanelStore((state) => state.setIsPanelOpen)
  const panelRef = useRef<HTMLDivElement>(null)
  const dropZoneRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const uploadMutation = useUploadFile()
  const createFolderMutation = useCreateFolder()

  const [queuedFiles, setQueuedFiles] = useState<QueuedFile[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const [isUploading, setIsUploading] = useState(false)

  // Sync global panel state (this disables the timer)
  useEffect(() => {
    setIsPanelOpen(isOpen)
    return () => setIsPanelOpen(false)
  }, [isOpen, setIsPanelOpen])

  // Helper to read all entries from a directory reader
  const readAllEntries = async (reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> => {
    const entries: FileSystemEntry[] = []
    let readEntries = await new Promise<FileSystemEntry[]>((resolve) => reader.readEntries(resolve))

    while (readEntries.length > 0) {
      entries.push(...readEntries)
      readEntries = await new Promise<FileSystemEntry[]>((resolve) => reader.readEntries(resolve))
    }

    return entries
  }

  // Recursive directory traversal
  const traverseDirectory = async (
    entry: FileSystemEntry,
    path = ''
  ): Promise<{ file: File; path: string }[]> => {
    if (entry.isFile) {
      const fileEntry = entry as FileSystemFileEntry
      return new Promise((resolve) => {
        fileEntry.file((file) => {
          resolve([{ file, path: path + file.name }])
        })
      })
    } else if (entry.isDirectory) {
      const dirEntry = entry as FileSystemDirectoryEntry
      const reader = dirEntry.createReader()
      const entries = await readAllEntries(reader)

      const results: { file: File; path: string }[] = []
      for (const childEntry of entries) {
        const children = await traverseDirectory(childEntry, path + entry.name + '/')
        results.push(...children)
      }
      return results
    }
    return []
  }

  // Handle initial files (from external drag)
  useEffect(() => {
    if (initialFiles && initialFiles.length > 0) {
      // Filter out 0-byte folder entries
      const validFiles = initialFiles.filter((file) => !(file.size === 0 && file.type === ''))
      const newFiles: QueuedFile[] = validFiles.map((file) => ({
        id: crypto.randomUUID(),
        file,
        relativePath: file.name,
        progress: 0,
        status: 'pending',
      }))
      setQueuedFiles((prev) => [...prev, ...newFiles])
    }
  }, [initialFiles])

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isUploading) onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose, isUploading])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    const items = Array.from(e.dataTransfer.items)
    const queue: QueuedFile[] = []

    for (const item of items) {
      // WebkitGetAsEntry is the key API for folders
      const entry = item.webkitGetAsEntry?.() || null

      if (entry) {
        if (entry.isFile) {
          const file = item.getAsFile()
          if (file) {
            queue.push({
              id: crypto.randomUUID(),
              file,
              relativePath: file.name,
              progress: 0,
              status: 'pending',
            })
          }
        } else if (entry.isDirectory) {
          // Recursive folder traversal
          const files = await traverseDirectory(entry as FileSystemEntry)
          files.forEach(({ file, path }) => {
            queue.push({
              id: crypto.randomUUID(),
              file,
              relativePath: path,
              progress: 0,
              status: 'pending',
            })
          })
        }
      } else {
        // Fallback for non-webkit browsers
        const file = item.getAsFile()
        if (file) {
          // Skip likely folders (0 bytes, empty type) that failed webkit entry check
          if (file.size === 0 && file.type === '') {
            console.warn('Skipping potential folder or empty file:', file.name)
            continue
          }
          queue.push({
            id: crypto.randomUUID(),
            file,
            relativePath: file.name,
            progress: 0,
            status: 'pending',
          })
        }
      }
    }

    if (queue.length > 0) {
      setQueuedFiles((prev) => [...prev, ...queue])
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleBrowseClick = () => {
    document.getElementById('upload-panel-file-input')?.click()
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : []
    if (files.length > 0) {
      const newFiles: QueuedFile[] = files.map((file) => ({
        id: crypto.randomUUID(),
        file,
        relativePath: file.name,
        progress: 0,
        status: 'pending',
      }))
      setQueuedFiles((prev) => [...prev, ...newFiles])
    }
    e.target.value = ''
  }

  const removeFile = (id: string) => {
    setQueuedFiles((prev) => prev.filter((f) => f.id !== id))
  }

  // Create folder hierarchy for files with relative paths
  const createFolderHierarchy = async (files: QueuedFile[]): Promise<Map<string, string>> => {
    // Collect all unique folder paths
    const folderPaths = new Set<string>()
    files.forEach((item) => {
      const parts = item.relativePath.split('/')
      if (parts.length > 1) {
        // Generate all parent paths: "A/B/C/file.txt" -> "A", "A/B", "A/B/C"
        let currentPath = ''
        for (let i = 0; i < parts.length - 1; i++) {
          currentPath += (currentPath ? '/' : '') + parts[i]
          folderPaths.add(currentPath)
        }
      }
    })

    // Sort by depth (shortest first) so we create parents before children
    const sortedPaths = Array.from(folderPaths).sort(
      (a, b) => a.split('/').length - b.split('/').length
    )

    // Map path -> folderId
    const folderIdMap = new Map<string, string>()

    for (const path of sortedPaths) {
      const parts = path.split('/')
      const folderName = parts[parts.length - 1]
      const parentPath = parts.slice(0, -1).join('/')

      // Determine parent ID: either from our map (created subfolder) or the prop (current root)
      const parentId = parentPath ? folderIdMap.get(parentPath) : folderId

      try {
        // Check if we already created it (in this map) - redundant but safe
        if (folderIdMap.has(path)) continue

        // Create folder
        const newFolder = await createFolderMutation.mutateAsync({
          workspaceSlug,
          name: folderName,
          parentId: parentId || null,
        })

        if (newFolder?.id) {
          folderIdMap.set(path, newFolder.id)
        }
      } catch (err) {
        console.error(`Failed to create folder ${path}`, err)
        // Continue - files will go to parent folder if this fails
      }
    }

    return folderIdMap
  }

  const handleUpload = async () => {
    if (queuedFiles.length === 0 || !workspaceSlug) return

    setIsUploading(true)

    // First, create folder structure if any files have relative paths with folders
    const folderMap = await createFolderHierarchy(queuedFiles)

    for (const qFile of queuedFiles) {
      // Skip already processed
      if (qFile.status === 'done' || qFile.status === 'error') continue

      // Determine target folder ID based on relative path
      let targetFolderId = folderId || null
      const parts = qFile.relativePath.split('/')
      if (parts.length > 1) {
        const parentPath = parts.slice(0, -1).join('/')
        if (folderMap.has(parentPath)) {
          targetFolderId = folderMap.get(parentPath) || null
        }
      }

      // Mark as uploading
      setQueuedFiles((prev) =>
        prev.map((f) => (f.id === qFile.id ? { ...f, status: 'uploading', progress: 0 } : f))
      )

      try {
        await uploadMutation.mutateAsync({
          workspaceSlug,
          file: qFile.file,
          folderId: targetFolderId,
          onProgress: (progress) => {
            setQueuedFiles((prev) => prev.map((f) => (f.id === qFile.id ? { ...f, progress } : f)))
          },
        })

        // Mark as done
        setQueuedFiles((prev) =>
          prev.map((f) => (f.id === qFile.id ? { ...f, status: 'done', progress: 100 } : f))
        )
      } catch (error: any) {
        console.error('Upload failed:', error)
        setQueuedFiles((prev) =>
          prev.map((f) => (f.id === qFile.id ? { ...f, status: 'error', error: error.message } : f))
        )
      }
    }

    setIsUploading(false)

    // Check if all succeeded
    const allDone = queuedFiles.every((f) => f.status === 'done')
    if (allDone) {
      setTimeout(() => {
        setQueuedFiles([])
        onUploadComplete?.()
        onClose()
      }, 500)
    }
  }

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return '🖼️'
    if (file.type.includes('pdf')) return '📄'
    if (file.type.includes('spreadsheet') || file.type.includes('excel')) return '📊'
    if (file.type.includes('document') || file.type.includes('word')) return '📝'
    if (file.type.startsWith('video/')) return '🎬'
    if (file.type.startsWith('audio/')) return '🎵'
    return '📁'
  }

  const pendingCount = queuedFiles.filter(
    (f) => f.status === 'pending' || f.status === 'uploading'
  ).length

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={() => !isUploading && onClose()}
      />

      {/* Panel - Side panel style like other panels */}
      <div
        ref={panelRef}
        className={`fixed inset-0 z-50 flex w-full max-w-none transform flex-col rounded-none border border-white/5 bg-[var(--app-bg-card)] shadow-2xl transition-transform duration-300 ease-out sm:inset-auto sm:bottom-4 sm:right-4 sm:top-4 sm:w-[448px] sm:max-w-md sm:rounded-2xl ${isOpen ? 'translate-x-0' : 'translate-x-[calc(100%+2rem)]'}`}
      >
        {/* Header */}
        <div className="flex-none border-b border-white/5 p-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => !isUploading && onClose()}
              disabled={isUploading}
              className="rounded-lg p-2 text-[var(--app-text-muted)] transition-colors hover:bg-[var(--app-bg-elevated)] hover:text-[var(--app-text-primary)] disabled:opacity-50"
            >
              <ChevronDoubleRightIcon />
            </button>
            <div>
              <h2 className="text-lg font-semibold text-[var(--app-text-primary)]">
                {t('files.messages.upload_files')}
              </h2>
              <p className="text-xs text-[var(--app-text-muted)]">
                {t('files.messages.add_files_desc')}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="custom-scrollbar flex-1 overflow-y-auto p-6">
          {/* Drop Zone */}
          <div
            ref={dropZoneRef}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleBrowseClick}
            className={`relative mb-6 flex cursor-pointer flex-col items-center justify-center rounded-none border-2 border-dashed px-6 py-12 transition-all sm:rounded-xl ${
              isDragOver
                ? 'bg-[var(--app-accent)]/10 border-[var(--app-accent)]'
                : 'bg-[var(--app-bg-elevated)]/30 hover:bg-[var(--app-bg-elevated)]/50 border-white/5 hover:border-white/10'
            } `}
          >
            <div
              className={`mb-4 flex h-14 w-14 items-center justify-center rounded-full transition-colors ${isDragOver ? 'bg-[var(--app-accent)]/20' : 'bg-[var(--app-bg-elevated)]'}`}
            >
              <Upload
                className={`h-6 w-6 transition-colors ${isDragOver ? 'text-[var(--app-accent)]' : 'text-[var(--app-text-muted)]'}`}
              />
            </div>
            <p className="text-center text-sm text-[var(--app-text-secondary)]">
              {t('files.messages.drag_drop_files_folders')}
            </p>
            <p className="mt-1 text-center text-sm font-semibold text-[var(--app-accent)]">
              {t('files.messages.click_to_browse')}
            </p>
            <input
              type="file"
              id="upload-panel-file-input"
              multiple
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>

          {/* File List */}
          {queuedFiles.length > 0 && (
            <div className="space-y-2">
              {queuedFiles.map((qFile) => (
                <div
                  key={qFile.id}
                  className="bg-[var(--app-bg-elevated)]/50 group flex items-center gap-3 rounded-none border border-white/5 p-3 sm:rounded-xl"
                >
                  {/* File Icon */}
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-white/5 bg-[var(--app-bg-card)] text-lg">
                    {getFileIcon(qFile.file)}
                  </div>

                  {/* File Info */}
                  <div className="min-w-0 flex-1">
                    <p
                      className="truncate text-sm font-medium text-[var(--app-text-primary)]"
                      title={qFile.relativePath}
                    >
                      {qFile.relativePath}
                    </p>
                    <p className="text-xs text-[var(--app-text-muted)]">
                      {formatFileSize(qFile.file.size)}
                    </p>

                    {/* Progress Bar */}
                    {qFile.status === 'uploading' && (
                      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[var(--app-bg-elevated)]">
                        <div
                          className="h-full rounded-full bg-[var(--app-accent)] shadow-[0_0_8px_var(--app-accent)] transition-all duration-200"
                          style={{ width: `${qFile.progress}%` }}
                        />
                      </div>
                    )}

                    {/* Error message */}
                    {qFile.status === 'error' && (
                      <p className="mt-1 text-xs text-red-400">
                        {qFile.error || t('files.messages.upload_failed')}
                      </p>
                    )}
                  </div>

                  {/* Status/Actions */}
                  {qFile.status === 'done' ? (
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-500/10">
                      <svg
                        className="h-4 w-4 text-green-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  ) : qFile.status === 'uploading' ? (
                    <div className="bg-[var(--app-accent)]/10 flex h-8 w-8 items-center justify-center rounded-lg">
                      <Loader2 size={16} className="animate-spin text-[var(--app-accent)]" />
                    </div>
                  ) : qFile.status === 'error' ? (
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-500/10">
                      <svg
                        className="h-4 w-4 text-red-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        removeFile(qFile.id)
                      }}
                      className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--app-bg-elevated)] text-[var(--app-text-muted)] opacity-0 transition-colors hover:bg-red-500/10 hover:text-red-500 group-hover:opacity-100"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-none border-t border-white/5 bg-[var(--app-bg-card)] p-6">
          <button
            onClick={handleUpload}
            disabled={pendingCount === 0 || isUploading}
            className="w-full rounded-none bg-[var(--app-accent)] py-3.5 font-bold text-white shadow-lg transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 sm:rounded-xl"
          >
            {isUploading
              ? t('files.picker.uploading')
              : pendingCount > 0
                ? t('files.picker.upload_count', { count: pendingCount })
                : t('files.actions.upload')}
          </button>
        </div>
      </div>
    </>,
    document.body
  )
}
